import type { RouteRecord } from "vite-react-ssg";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Home from "./pages/Home";
import { LANDING_PAGES } from "./lib/landingData";
import { BLOG_POSTS } from "./lib/blogData";
import { trackRedditMarketingVisit } from "./lib/redditPixel";

/**
 * Resets scroll to the top whenever the route changes. Without this, React
 * Router preserves the previous scroll position on client-side navigation, so
 * clicking a use-case card near the bottom of the homepage lands the next page
 * already scrolled down to its footer.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/**
 * Captures a referral code from `?ref=` on the marketing site and stashes it
 * under the `pulse_ref` key. Referral links now point at the clean root domain
 * (meriana.app/?ref=CODE) instead of the app, so the code must survive the
 * same-origin hop to /app, where the app reads `pulse_ref` at signup. We write
 * both sessionStorage (same-tab navigation) and localStorage (shared across
 * tabs on the same origin) so attribution holds even if the app opens in a new
 * tab. The app clears `pulse_ref` after a successful signup.
 */
function RefCapture() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      // sessionStorage covers the same-tab hop; localStorage (shared across tabs
      // on the same origin) covers the app opening in a new tab.
      try { sessionStorage.setItem("pulse_ref", ref); } catch { /* storage blocked */ }
      try { localStorage.setItem("pulse_ref", ref); } catch { /* storage blocked */ }
      // Cookie too: Google OAuth signups complete server-side, where web storage
      // never arrives — the pulse_ref cookie is the only channel that reaches
      // the API callback. Cleared by the server after a successful signup.
      try {
        document.cookie = "pulse_ref=" + encodeURIComponent(ref) +
          "; path=/; max-age=" + 30 * 24 * 3600 + "; SameSite=Lax";
      } catch { /* cookies blocked */ }
    }
    captureAttribution();
    trackMarketingVisit();
    void trackRedditMarketingVisit();
  }, []);
  return null;
}

function getVisitorId(): string {
  const existing = document.cookie
    .split("; ")
    .find((c) => c.startsWith("pulse_visitor="))
    ?.slice("pulse_visitor=".length);
  if (existing && /^[a-zA-Z0-9_-]{16,80}$/.test(existing)) return existing;

  const fallback = "v_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 14);
  const id = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : fallback;
  document.cookie = "pulse_visitor=" + encodeURIComponent(id) +
    "; path=/; max-age=" + 180 * 24 * 3600 + "; SameSite=Lax";
  return id;
}

/** Record one anonymous public-site visit. The API deduplicates rapid reloads. */
function trackMarketingVisit() {
  try {
    const p = new URLSearchParams(window.location.search);
    let referrer = "";
    try {
      if (document.referrer) {
        const h = new URL(document.referrer).hostname;
        if (h && h !== window.location.hostname) referrer = h;
      }
    } catch { /* invalid referrer */ }
    const payload: Record<string, string> = {
      visitor_id: getVisitorId(),
      path: window.location.pathname || "/",
    };
    if (referrer) payload.referrer = referrer;
    for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
      const value = p.get(key);
      if (value) payload[key] = value.slice(0, 100);
    }
    fetch("/api/analytics/marketing-visit", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch { /* measurement must never break the page */ }
}

/** First-touch marketing attribution: record UTM params, the external referrer
 *  hostname, and the landing path in a 30-day first-party cookie (`pulse_attrib`).
 *  The API reads it at signup (both email and Google paths) and stores it on the
 *  user. A fresh UTM-tagged visit overrides an older touch; otherwise first
 *  touch wins. */
function captureAttribution() {
  try {
    const p = new URLSearchParams(window.location.search);
    const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
    const hasUtm = utmKeys.some((k) => p.get(k));
    const clickId = p.get("fbclid");
    let allowMetaClick = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl !== true;
    try { if (localStorage.getItem("meriana_ad_optout") === "1") allowMetaClick = false; } catch { allowMetaClick = false; }
    const hasMetaClick = allowMetaClick && !!clickId && /^[A-Za-z0-9._~-]{1,200}$/.test(clickId);
    const already = document.cookie.split("; ").some((c) => c.startsWith("pulse_attrib="));
    if (already && !hasUtm && !hasMetaClick) return;
    let refHost = "";
    try {
      if (document.referrer) {
        const h = new URL(document.referrer).hostname;
        if (h && h !== window.location.hostname) refHost = h;
      }
    } catch { /* invalid referrer */ }
    if (!hasUtm && !hasMetaClick && !refHost && already) return;
    const data: Record<string, string> = {};
    for (const k of utmKeys) { const v = p.get(k); if (v) data[k] = v.slice(0, 200); }
    if (hasMetaClick) data.fbclid = clickId!;
    if (refHost) data.referrer = refHost;
    data.landing = window.location.pathname;
    data.ts = new Date().toISOString();
    document.cookie = "pulse_attrib=" + encodeURIComponent(JSON.stringify(data)) +
      "; path=/; max-age=" + 30 * 24 * 3600 + "; SameSite=Lax";
  } catch { /* never break the page over attribution */ }
}

const withScroll = (el: ReactNode) => (
  <>
    <ScrollToTop />
    <RefCapture />
    {el}
  </>
);

/**
 * Route-level code splitting. Each non-home page is loaded via react-router's
 * `lazy` so the client bundle for `/` doesn't include every page. vite-react-ssg
 * awaits these during the SSG build, so the prerendered HTML is still complete.
 */
const lazyPage = (
  load: () => Promise<ReactNode>,
): (() => Promise<{ Component: () => ReactNode }>) => {
  return async () => {
    const el = await load();
    return { Component: () => withScroll(el) };
  };
};

export const routes: RouteRecord[] = [
  { path: "/", element: withScroll(<Home />) },
  ...Object.values(LANDING_PAGES).map((data) => ({
    path: data.meta.slug,
    lazy: lazyPage(async () => {
      const m = await import("./components/LandingPage");
      return <m.default data={data} />;
    }),
  })),
  {
    path: "/frontpage",
    lazy: lazyPage(async () => {
      const m = await import("./pages/FrontpageGame");
      return <m.default />;
    }),
  },
  {
    path: "/games",
    lazy: lazyPage(async () => {
      const m = await import("./pages/Games");
      return <m.default />;
    }),
  },
  {
    path: "/pricing",
    lazy: lazyPage(async () => {
      const m = await import("./pages/Pricing");
      return <m.default />;
    }),
  },
  {
    path: "/faq",
    lazy: lazyPage(async () => {
      const m = await import("./pages/Faq");
      return <m.default />;
    }),
  },
  {
    path: "/blog",
    lazy: lazyPage(async () => {
      const m = await import("./pages/Blog");
      return <m.default />;
    }),
  },
  ...BLOG_POSTS.map((post) => ({
    path: `/blog/${post.slug}`,
    lazy: lazyPage(async () => {
      const m = await import("./components/BlogPost");
      return <m.default post={post} />;
    }),
  })),
  {
    path: "/privacy",
    lazy: lazyPage(async () => {
      const m = await import("./pages/Privacy");
      return <m.default />;
    }),
  },
  {
    path: "/terms",
    lazy: lazyPage(async () => {
      const m = await import("./pages/Terms");
      return <m.default />;
    }),
  },
  {
    path: "*",
    lazy: lazyPage(async () => {
      const m = await import("./pages/not-found");
      return <m.default />;
    }),
  },
];
